/**
 * User Management Page
 * Manage authorized users and their role-based permissions
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AdminPanelSettings as AdminIcon,
  Business as TenantIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useForm, Controller } from 'react-hook-form';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/apiClient';
import { useNotification } from '../contexts/NotificationContext';
import UserRoleAssignment from '../components/admin/UserRoleAssignment';
import ConfirmDialog from '../components/common/ConfirmDialog';
import SortableHeaderCell from '../components/common/SortableHeaderCell';
import useTableSort from '../hooks/useTableSort';
import logger from '../utils/logger';

const USER_COLUMNS = [
  { key: 'email',      label: 'Email',      align: 'left', getValue: (u) => u.email },
  { key: 'tenants',    label: 'Tenants',    align: 'left', getValue: (u) => (u.tenants || []).length },
  { key: 'roles',      label: 'Roles',      align: 'left', sortable: false },
  { key: 'last_login', label: 'Last Login', align: 'left', getValue: (u) => u.last_login },
];

function UserManagement() {
  const notify = useNotification();
  const [users, setUsers] = useState([]);
  const sort = useTableSort();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userRolesCache, setUserRolesCache] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  const { control, handleSubmit, reset, setValue } = useForm();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      // Try to get users with tenant info first
      const response = await apiGet('/admin/users-with-tenants');
      let userList = [];
      if (response.success && response.data) {
        // Filter to internal users only - external clients are managed on the Approvals page
        userList = response.data.filter(u => !u.user_type || u.user_type === 'internal' || u.user_type === 'internal_staff');
      } else {
        // Fallback to basic user list
        const data = await apiGet('/admin/authorized-users');
        const allUsers = Array.isArray(data) ? data.map(u => ({ ...u, tenants: [] })) : [];
        userList = allUsers.filter(u => !u.user_type || u.user_type === 'internal' || u.user_type === 'internal_staff');
      }

      setUsers(userList);

      // Load roles for each user
      const rolesCache = {};
      for (const user of userList) {
        try {
          const rolesResponse = await apiGet(`/admin/users/${user.id}/roles`);
          rolesCache[user.id] = rolesResponse.data || [];
        } catch {
          rolesCache[user.id] = [];
        }
      }
      setUserRolesCache(rolesCache);
    } catch (err) {
      logger.error('Error loading users', { message: err.message, status: err.response?.status });
      // Fallback to basic endpoint
      try {
        const data = await apiGet('/admin/authorized-users');
        const allUsers = Array.isArray(data) ? data.map(u => ({ ...u, tenants: [] })) : [];
        setUsers(allUsers.filter(u => !u.user_type || u.user_type === 'internal'));
      } catch {
        setUsers([]);
      }
    }
  };

  const handleOpenDialog = async (user = null) => {
    setEditingUser(user);
    if (user) {
      setValue('email', user.email);
    } else {
      reset();
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    reset();
  };

  const onSubmit = async (data) => {
    try {
      const payload = {
        email: data.email,
        // Permissions are managed via roles, not passed here
      };

      if (editingUser) {
        await apiPut(`/admin/users/${editingUser.id}`, payload);
      } else {
        await apiPost('/admin/users', payload);
      }

      await loadUsers();
      handleCloseDialog();
    } catch (err) {
      logger.error('Error saving user', { message: err.message, status: err.response?.status });
    }
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      open: true,
      title: 'Delete User',
      message: 'Are you sure you want to delete this user?',
      onConfirm: async () => {
        try {
          await apiDelete(`/admin/users/${id}`);
          setConfirmDialog(prev => ({ ...prev, open: false }));
          await loadUsers();
        } catch (err) {
          logger.error('Error deleting user', { message: err.message, status: err.response?.status });
          setConfirmDialog(prev => ({ ...prev, open: false }));
          notify.error('Failed to delete user: ' + (err.response?.data?.error?.message || err.response?.data?.message || err.message));
        }
      },
    });
  };

  const handleRoleUpdate = () => {
    // Refresh user list when roles/tenants change
    loadUsers();
  };

  // Get role summary for display
  const getRoleSummary = (userId) => {
    const roles = userRolesCache[userId] || [];
    if (roles.length === 0) {
      return null;
    }
    return roles;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">User Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add User
        </Button>
      </Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Manage internal staff users and their permissions. Users are authenticated via Azure Easy Auth.
            Permissions are now role-based - assign roles to users, then fine-tune with overrides if needed.
            External client portal users are managed on the Approvals page.
          </Typography>
        </CardContent>
      </Card>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {USER_COLUMNS.map((col) => (
                col.sortable === false ? (
                  <TableCell key={col.key} align={col.align}>{col.label}</TableCell>
                ) : (
                  <SortableHeaderCell key={col.key} sort={sort} columnKey={col.key} align={col.align}>
                    {col.label}
                  </SortableHeaderCell>
                )
              ))}
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sort.sortRows(users, USER_COLUMNS).map((user) => {
              const userTenants = user.tenants || [];
              const roles = getRoleSummary(user.id);

              return (
                <TableRow key={user.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AdminIcon fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight="medium">
                        {user.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {userTenants.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          No tenants
                        </Typography>
                      ) : (
                        userTenants.map((tenant) => (
                          <Chip
                            key={tenant.tenant_id}
                            icon={<TenantIcon />}
                            label={
                              <span>
                                {tenant.tenant_name || tenant.tenant_code}
                                {tenant.is_admin && ' (Admin)'}
                              </span>
                            }
                            size="small"
                            color={tenant.is_admin ? 'primary' : 'default'}
                          />
                        ))
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {!roles || roles.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          No roles assigned
                        </Typography>
                      ) : (
                        roles.map((role) => (
                          <Chip
                            key={role.role_id}
                            icon={<SecurityIcon />}
                            label={role.name}
                            size="small"
                            color={role.is_system_role ? 'warning' : 'primary'}
                            variant={role.is_system_role ? 'filled' : 'outlined'}
                          />
                        ))
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {user.last_login
                      ? format(new Date(user.last_login), 'dd MMM yyyy HH:mm')
                      : 'Never'}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(user)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(user.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="textSecondary" sx={{ py: 3 }}>
                    No users found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Add/Edit User Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingUser ? `Edit User: ${editingUser.email}` : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          {!editingUser ? (
            // Add new user - just email field
            (<form>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid size={12}>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Email Address *"
                        type="email"
                        error={!!error}
                        helperText={error?.message || 'User must have an Azure AD account with this email'}
                      />
                    )}
                  />
                </Grid>
                <Grid size={12}>
                  <Alert severity="info">
                    After creating the user, edit them to assign roles and permissions.
                  </Alert>
                </Grid>
              </Grid>
            </form>)
          ) : (
            // Edit existing user - show role assignment with tenants
            (<Box sx={{ mt: 2 }}>
              <UserRoleAssignment
                userId={editingUser.id}
                userEmail={editingUser.email}
                userTenants={editingUser.tenants || []}
                onUpdate={handleRoleUpdate}
              />
            </Box>)
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {editingUser ? 'Close' : 'Cancel'}
          </Button>
          {!editingUser && (
            <Button onClick={handleSubmit(onSubmit)} variant="contained">
              Create
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Confirm"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
}

export default UserManagement;
